import CryptoJS from "crypto-js";
import * as legacyTLMD from "./auth-legacy.js";
import { UTM } from "../map/projection.js";
import { getPolygonArea, getPolygonAverageVertex, getPolygonCCW } from "../geometry/polygon.js";
import { okhsl } from "../d3-mapp/color/okhsl.js";
import { LEGACY_DATA_COLOR_INTERPOLATOR } from "../d3-mapp/color/legacy-colors.js";
import { interpolateOkhsl } from "../d3-mapp/interpolate/interpolate-okhsl.js";
//let tlmd: any;
let _config;
export const API = {
    init: (host, useLocalStorage = true) => {
        _config = {
            digester: new legacyTLMD.SHA256Digester(useLocalStorage ? legacyTLMD.legacyCredentialStorage : undefined),
            domain: "system",
            host,
        };
    },
    setDomain: (domain) => {
        _config.domain = domain;
    },
    reloadCredentials: () => {
        _config.digester.reloadCredentials();
    },
    setCredentials: (usr, pwd) => {
        _config.digester.clear();
        if (usr && pwd) {
            _config.digester.setCredentials(`${usr}::${CryptoJS.SHA256(pwd).toString(CryptoJS.enc.Hex)}`);
        }
        else if (usr && !pwd) {
            _config.digester.setCredentials(usr);
        }
    },
    signIn: async () => {
        try {
            const result = await legacyTLMD.xhrPromise({
                host: _config.host,
                url: `/${_config.domain}/signin`,
                method: legacyTLMD.Method.get,
                digester: _config.digester,
            });
            return JSON.parse(result.data);
        }
        catch (error) {
            return API.permissions();
        }
    },
    permissions: async () => {
        const result = await legacyTLMD.xhrPromise({
            host: _config.host,
            url: `/${_config.domain}/permissions`,
            method: legacyTLMD.Method.get,
            digester: _config.digester,
        });
        return JSON.parse(result.data);
    },
    get: async (url, responseType = legacyTLMD.ResponseType.json) => legacyTLMD
        .xhrPromise({
        host: _config.host,
        url: `/${_config.domain}${url}`,
        method: legacyTLMD.Method.get,
        responseType: responseType,
        digester: _config.digester,
    })
        .then((response) => response.data),
    post: async (url, payload, mimeType) => legacyTLMD
        .xhrPromise({
        host: _config.host,
        url: `/${_config.domain}${url}`,
        method: legacyTLMD.Method.post,
        responseType: legacyTLMD.ResponseType.json,
        data: payload
            ? {
                entity: payload.pop ? payload.join(" ") : payload,
                mimeType: mimeType ? mimeType : "text/plain",
            }
            : undefined,
        digester: _config.digester,
    })
        .then((response) => response.data),
    getSensorFeedTypes: async () => API.get(`/sensor/api/types`),
    getSensorFeedType: async (feedTypeId) => API.get(`/sensor/api/types/${feedTypeId}`),
    getAllSensorProbes: async () => API.get(`/sensor/api/probes`),
    getSensorProbes: async (probeIds) => API.post(`/sensor/api/probes`, probeIds),
    getSensorProbe: async (sensorProbeId) => API.get(`/sensor/api/probes/${sensorProbeId}`),
    getSensorFeeds: async (start, end, feedTypeIds) => API.post(`/sensor/api/feeds?rc=1&s0=${start}&e0=${end}`, feedTypeIds),
    getSensorFeedsOfType: async (start, end, feedTypeId) => API.get(`/sensor/api/feeds/${feedTypeId}?rc=1&s0=${start}&e0=${end}`),
    getSensorDecodedData: async (start, end, feedIdsRefs, intervalInSeconds) => API.post(`/sensor/api/feeds/data?rc=1&s0=${start}&e0=${end}&avg=${intervalInSeconds}`, feedIdsRefs),
    getSensorEncodedData: async (start, end, feedIdsRefs) => API.post(`/sensor/api/feeds/data?rc=1&s0=${start}&e0=${end}`, feedIdsRefs),
    getObservationProbes: async () => API.get(`/observation/api/probes`),
    getObservationProbe: async (observationProbeId) => API.get(`/observation/api/probes/${observationProbeId}`),
    getObservationTypes: async () => API.get(`/observation/api/types`),
    getObservationType: async (observationTypeId) => API.get(`/observation/api/types/${observationTypeId}`),
    getObservationTypeDefinition: async (observationTypeId) => API.get(`/observation/api/types/${observationTypeId}?full=true`),
    getObservations: async (start, end, observationTypeIds) => API.post(`/observation/api/observations?rc=1&s0=${start}&e0=${end}`, observationTypeIds),
    getModellingTypes: async () => API.get(`/modeling/api/types`),
    getModellingType: async (modellingTypeId) => API.get(`/modeling/api/types/${modellingTypeId}`),
    getModellingRuns: async (start, end, modellingTypeIds, includeAnalysisResults = false, active, deleted) => API.post(`/modeling/api/runs?rc=1&s0=${start}&e0=${end}&deleted=${deleted}${active !== undefined ? `&active=${active}` : ""}&analysis=${includeAnalysisResults}`, modellingTypeIds),
    getWindField: async (hpc, model, start) => API.get(`/modeling/windfield/${hpc}/${model}?start=${typeof start === "number" ? new Date(start).toISOString() : start.toISOString()}`, legacyTLMD.ResponseType.arraybuffer),
    getGoiTypes: async () => API.get(`/poi/api/types`),
    getGoiType: async (goiTypeId) => API.get(`/poi/api/types/${goiTypeId}`),
    getGois: async (start, end, goiTypeIds, active, deleted) => {
        const gois = await API.post(`/poi/api/gois?rc=1&s0=${start}&e0=${end}&deleted=${deleted}${active !== undefined ? `&active=${active}` : ""}`, goiTypeIds);
        return convertRawGoiToGoiDefinition(gois);
    },
    getGoisOfType: async (goiTypeId, active, deleted) => {
        const gois = await API.get(`/poi/api/gois/${goiTypeId}?deleted=${deleted}${active !== undefined ? `&active=${active}` : ""}`);
        return convertRawGoiToGoiDefinition(gois);
    },
};
/**
 * Enhances the GeometryOfInterest definitions coming from the server
 * @param rawGois the raw definitions coming from the server
 * @returns array of GOIDefinitions
 */
const convertRawGoiToGoiDefinition = (rawGois) => {
    if (rawGois && rawGois.length > 0) {
        const utm = rawGois[0].geometry.lat !== undefined
            ? UTM.get(rawGois[0].geometry.lat, rawGois[0].geometry.lng)
            : UTM.get(rawGois[0].geometry.coordinates[0].lat, rawGois[0].geometry.coordinates[0].lng);
        const metaExpr = new RegExp("{([^}]+)}", "g");
        const nameExpr = new RegExp("[^}]*$", "g");
        return rawGois.map((goi, i) => {
            const isArea = !!goi.geometry.coordinates;
            const meta = Array.from(goi.name.matchAll(metaExpr)).map((match) => match[1]);
            const nameMatch = goi.name.match(nameExpr);
            goi.name = nameMatch ? nameMatch[0] : goi.name;
            const isHole = meta.filter((property) => property.toLowerCase() === "hole")
                .length === 1 && isArea;
            const metaColors = meta
                .filter((property) => property.startsWith("#"))
                .map((colorProperty) => okhsl(colorProperty));
            goi.autoColor = okhsl(LEGACY_DATA_COLOR_INTERPOLATOR(interpolateOkhsl)(i / rawGois.length));
            goi.color = metaColors.length > 0 ? metaColors[0] : goi.autoColor.copy();
            goi.meta = meta.filter((property) => property.toLowerCase() !== "hole" || property.startsWith("#"));
            ;
            if (isArea) {
                //an area
                goi.isHole = isHole;
                const area = goi.geometry;
                area.xy = getPolygonCCW(area.coordinates.map((geodetic) => utm.toCartesian(geodetic.lat, geodetic.lng)));
                area.size = getPolygonArea(area.xy.slice(0, -1));
                area.geodeticBounds = area.coordinates.reduce((bounds, coord) => {
                    bounds.southWest.lat = Math.min(bounds.southWest.lat, coord.lat);
                    bounds.southWest.lng = Math.min(bounds.southWest.lng, coord.lng);
                    bounds.northEast.lat = Math.max(bounds.northEast.lat, coord.lat);
                    bounds.northEast.lng = Math.max(bounds.northEast.lng, coord.lng);
                    return bounds;
                }, { southWest: area.coordinates[0], northEast: area.coordinates[0] });
                area.utmBounds = area.xy.reduce((bounds, coord) => {
                    bounds.southWest.y = Math.min(bounds.southWest.y, coord[1]);
                    bounds.southWest.x = Math.min(bounds.southWest.x, coord[0]);
                    bounds.northEast.y = Math.max(bounds.northEast.y, coord[1]);
                    bounds.northEast.x = Math.max(bounds.northEast.x, coord[0]);
                    return bounds;
                }, {
                    southWest: { x: area.xy[0][0], y: area.xy[0][1] },
                    northEast: { x: area.xy[0][0], y: area.xy[0][1] },
                });
                area.utmAverage = getPolygonAverageVertex(area.xy.slice(0, -1));
                area.geodeticAverage = utm.toGeodetic(area.utmAverage);
            }
            else {
                //a location
                goi.isHole = false;
                const location = goi.geometry;
                location.xy = utm.toCartesian(location.lat, location.lng);
            }
            return goi;
        });
    }
    else {
        return rawGois;
    }
};
